import { createSelector } from "reselect";
import RootState from "~/types/store";
import Immutable from "immutable";
import * as models from "./models";
import { NonFieldFeature } from "@ai360/core/dist/4x/es/api/non-field-feature";

export const CUSTOMER_DATA_KEY = "CUSTOMER_DATA";

const _getModuleState = (state: RootState): models.CustomerDataState => state[CUSTOMER_DATA_KEY];
export const getCustomerMap = (state: RootState) =>
    _getModuleState(state).customerMap as Immutable.Map<string, models.CustomerInfo>;
export const getFieldMap = (state: RootState) =>
    _getModuleState(state).fieldMap as Immutable.Map<string, models.FieldInfo>;
export const getOrgLevelMap = (state: RootState) => _getModuleState(state).orgLevels;
export const getSelectedFieldGuids = (state: RootState) => _getModuleState(state).selectedFields;
export const getSelectedFieldsMap = (state: RootState) => _getModuleState(state).selectedFieldsMap;
export const getSelectedFieldsCount = (state: RootState) => getSelectedFieldGuids(state).size;
export const getFetchingCustomerFields = (state: RootState) =>
    _getModuleState(state).fetchingCustomerFields;
export const getHasFields = (state: RootState, customerId: string) =>
    (_getModuleState(state).customerMap.get(customerId)?.fields ?? 0) > 0;
export const getLastCustomerPageId = (state: RootState) =>
    _getModuleState(state).lastCustomerFieldPageId;
export const getIsDoneFetchingCustomers = (state: RootState) =>
    _getModuleState(state).isDoneFetchingCustomers;
export const getFetchingSummary = (state: RootState) => _getModuleState(state).fetchingSummary;
export const getInitialEventCount = (state: RootState) => _getModuleState(state).initialEventCount;
export const getQuickSummary = (state: RootState) => _getModuleState(state).quickSummary;
export const getTotalFieldsCount = (state: RootState) => _getModuleState(state).totalFieldsCount;
export const getCustomerFieldMappings = (state: RootState) =>
    _getModuleState(state).customerFieldMappings;
/** fieldGuid -> customerGuid, rebuilt only when the mappings change; a field under two customers maps to "". */
const getCustomerGuidByFieldGuid = createSelector(getCustomerFieldMappings, (mappings) => {
    const customerByField = new Map<string, string>();
    for (const { fieldGuid, customerGuid } of mappings) {
        const existing = customerByField.get(fieldGuid);
        const isConflict = existing != null && existing !== customerGuid;
        customerByField.set(fieldGuid, isConflict ? "" : customerGuid);
    }
    return customerByField;
});

const NO_CUSTOMER_GUIDS: string[] = [];

/** Sorted distinct customers across `fieldGuids`; empty if there are none or ANY field is unmapped. */
const distinctCustomerGuids = (
    customerByField: Map<string, string>,
    fieldGuids: Iterable<string | null | undefined>
): string[] => {
    const customerGuids = new Set<string>();
    for (const fieldGuid of fieldGuids) {
        const customer = fieldGuid && customerByField.get(fieldGuid);
        if (!customer) {
            return NO_CUSTOMER_GUIDS;
        }
        customerGuids.add(customer);
    }
    return Array.from(customerGuids).sort();
};

/** `null` when the field isn't mapped (e.g. a batch template) -- crop callers then use `Catalog`. */
export const getCustomerGuidForField = (state: RootState, fieldGuid: string | null | undefined) =>
    (fieldGuid && getCustomerGuidByFieldGuid(state).get(fieldGuid)) || null;

/** New array each call, so hooks must select a joined key, not this array. Empty means `catalog`. */
export const getDistinctCustomerGuidsForFields = (
    state: RootState,
    fieldGuids: (string | null | undefined)[]
): string[] => distinctCustomerGuids(getCustomerGuidByFieldGuid(state), fieldGuids);

/** Sorted distinct customers of the selected fields; empty if none or any is unmapped. Stable while they're unchanged. */
export const getSelectedFieldsCustomerGuids = createSelector(
    getCustomerGuidByFieldGuid,
    getSelectedFieldGuids,
    distinctCustomerGuids
);
export const getFetchingFilteredFieldGuids = (state: RootState): boolean =>
    _getModuleState(state).fetchingFilteredFieldGuids;
export const getFilteredFieldGuids = (state: RootState): Immutable.Set<string> =>
    _getModuleState(state).filteredFieldGuids;
export const getAutoExpandedCustomers = (state: RootState): Immutable.Set<string> =>
    _getModuleState(state).autoExpandedCustomers;

export const getSelectedCustomerFields = createSelector(
    getCustomerFieldMappings,
    getSelectedFieldGuids,
    (customerFieldMappings, selectedFieldGuids) =>
        customerFieldMappings.filter((mapping) => selectedFieldGuids.has(mapping.fieldGuid))
);

export const getSelectedFieldGuidsForCustomer = createSelector(
    getSelectedCustomerFields,
    (_, customerGuid: string) => customerGuid,
    (selectedCustomerFields, customerGuid) =>
        selectedCustomerFields
            .filter((mapping) => mapping.customerGuid === customerGuid)
            .map((mapping) => mapping.fieldGuid)
);

export const getSelectedCustomerGuids = createSelector(
    getSelectedCustomerFields,
    (selectedCustomerFields) => selectedCustomerFields.map((f) => f.customerGuid)
);

export const getParsedOrgLevels = createSelector(getOrgLevelMap, (orgLevelMap) => {
    const orgLevels = [...orgLevelMap.values()];
    const { name } = orgLevels.find(({ orgLevelParents }) => orgLevelParents.length === 0) || {
        name: "",
    };
    return orgLevels.map((orgLevel) =>
        models.OrgLevelInfo.parseParentsSummary(
            orgLevel,
            orgLevel.orgLevelParents
                .filter((n) => n !== name)
                .reduce((s, o) => (s !== "" ? `${s} > ${o}` : o), "")
        )
    );
});

export const getNonFieldFeatures = (state: RootState): Immutable.Set<models.NonFieldFeatureInfo> =>
    Immutable.Set<models.NonFieldFeatureInfo>(
        _getModuleState(state)
            .nonFieldFeatureMap.valueSeq()
            .map((x) => x.feature)
    );

export const getVisibleNonFieldFeatures = (state: RootState): Immutable.Set<NonFieldFeature> =>
    _getModuleState(state)
        .nonFieldFeatureMap.reduce((accumulator, value) => {
            if (value.visible) {
                accumulator.add(value.feature);
            }

            return accumulator;
        }, Immutable.Set<NonFieldFeature>().asMutable())
        .asImmutable();

export const getSelectedNonFieldFeatures = (state: RootState): Immutable.Set<NonFieldFeature> =>
    _getModuleState(state)
        .nonFieldFeatureMap.reduce((accumulator, value) => {
            if (value.selected) {
                accumulator.add(value.feature);
            }

            return accumulator;
        }, Immutable.Set<NonFieldFeature>().asMutable())
        .asImmutable();

export const getHighlightedNonFieldFeatures = (state: RootState): Immutable.Set<NonFieldFeature> =>
    _getModuleState(state)
        .nonFieldFeatureMap.reduce((accumulator, value) => {
            if (value.highlighted) {
                accumulator.add(value.feature);
            }

            return accumulator;
        }, Immutable.Set<NonFieldFeature>().asMutable())
        .asImmutable();
